import { Box, Button, CircularProgress, CircularProgressProps, Typography } from '@mui/material';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Challenge } from '../../../../../interfaces/interfaces';
import { ChallengeComponentInterface, UiComponentElementInterface } from '../../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import EmptyComponent from '../../../pageBuilder/EmptyComponent';
import { TitleAndDescription } from '../../../pageBuilder/TitleAndDescription';

export function CircularProgressWithLabel(props: CircularProgressProps & { value: number }) {
  return (
    <Box position="relative" style={{ margin: '20px' }} display="inline-flex">
      <CircularProgress variant="determinate" style={{ height: '100px', width: '100px' }} {...props} />
      <Box top={0} left={0} bottom={0} right={0} position="absolute" display="flex" alignItems="center" justifyContent="center">
        <Typography variant="caption" component="div" color="textSecondary">{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
}

const ChallengeComponent = ({ uiComponent }: UiComponentElementInterface<ChallengeComponentInterface>) => {
  const { t } = useTranslation(['pagebuilder', 'event']);
  const [score, setScore] = useState<any>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [challenge, setChallenge] = useState<Challenge>();

  const user = useSelector((state) => state.common.user);
  const event = useSelector((state) => state.common.event);
  const challenges = useSelector((state) => state.common.challenges);

  const router = useRouter();

  async function fetch() {
    if (user && uiComponent?.challenge && !isLoading) {
      setIsLoading(true);
      try {
        // TODO move to services
        const { data: data } = await axios.get(`challenge/${uiComponent?.challenge}/score/${user._id}`);
        setScore(data.data.score);
      } catch (err) {
        console.error(err);
      }
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetch();
  }, [user]);

  useEffect(() => {
    if (score && uiComponent?.challenge) {
      const _ = challenge?.tasks.find((task) => task.goal > score[task._id]);
      setCanSubmit(!_);
    }
  }, [score, uiComponent?.challenge]);

  useEffect(() => {
    const _challenge = challenges.find((c) => c._id === uiComponent?.challenge);
    setChallenge(_challenge);
  }, [challenges, uiComponent?.challenge]);

  const calculateProgress = (task: any) => {
    if (!score || !score[task._id]) return 0;
    if (score[task._id] > task.goal) return 100;
    const _progress = (score[task._id] / task.goal) * 100;
    return _progress;
  };
  const submitScore = () => {
    setIsSubmitting(true);
    axios.post(`challenge/${uiComponent?.challenge}/score/submit`).then(({ data }) => {
      const submission = data.data;
      if (submission._id) {
        confetti();
        toast.success(t('pagebuilder:challenge-score-submit-success'));
      } else {
        confetti();
        toast.success(t('pagebuilder:challenge-score-submit-already'));
      }
      setHasSubmitted(true);
      setIsSubmitting(false);
    });
  };

  return (
    <>
      <TitleAndDescription component={uiComponent!} />
      {uiComponent?.challenge ? (
        <div
          className={`w-full`}
          style={{
            color: uiComponent?.styling?.textColor || '#000',
          }}>
          <div className="flex flex-row flex-wrap items-center justify-center w-full ">
            {!user && (
              <Button
                variant="contained"
                onClick={() => router.push(`${event?.eventauth?.registerPageUrl}?redirect=${btoa(unescape(encodeURIComponent(router.asPath)))}`)}>
                {t('event:register')}
              </Button>
            )}
            {user && isLoading && (
              <div className="flex flex-col items-center justify-center space-y-3">
                <CircularProgress />
                {t('pagebuilder:challenge-loading')}
              </div>
            )}
            {user && !isLoading && (
              <div className="flex flex-wrap justify-around w-full">
                {challenge?.tasks?.map((task, index) => (
                  <div
                    className="w-full flex flex-col justify-start sm:mx-0 mt-2.5 mb-12 p-2.5 border transition-all sm:w-[20vw] rounded-md shadow hover:shadow-md "
                    id={`task-${index}`}
                    key={`task-${index}`}>
                    <CircularProgressWithLabel value={calculateProgress(task)} />
                    <div className="flex flex-col space-y-2 break-all ">
                      <p className="text-lg font-bold">{task.name}</p>
                      <p className="text-sm font-bold">
                        {t('pagebuilder:challenge-score', {
                          score: Math.round(score?.[task._id] * 100) / 100 || 0,
                          scoregoal: task.goal,
                        })}
                      </p>
                      <p className="bg-blue-500">{task.description}</p>
                    </div>
                  </div>
                ))}
                <div className="grid w-full place-items-center">
                  {isSubmitting ? (
                    <CircularProgress />
                  ) : (
                    <Button
                      disabled={!canSubmit || hasSubmitted || user?.role !== 'PARTICIPANT_ANONYM'}
                      variant="contained"
                      color="secondary"
                      onClick={() => submitScore()}>
                      {user?.role === 'PARTICIPANT_ANONYM' ? t('pagebuilder:challenge-submit') : t('pagebuilder:challenge-only-exhibitor')}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <EmptyComponent componentName={t('pagebuilder:challenge')} />
      )}
    </>
  );
};

export default ChallengeComponent;
